/* eslint-disable jsdoc/require-jsdoc */
/**
 * Shared types and endpoint config for the TelosAir Insight Agent.
 *
 * The agentic loop (model calls and tool orchestration) now runs on the Node
 * AI server (telosair/ai-server) using the Vercel AI SDK. The browser streams
 * the conversation to and from that server via useChat, and executes the
 * agent tools locally (see ai-agent-tools.ts) because they need the user auth
 * token and the already-loaded dashboard data context.
 *
 * No model or OpenRouter API key is referenced here anymore. It lives only on
 * the server, so it is never shipped in the browser bundle.
 */
import { DEV_NEW_DOMAIN_URL } from '../environment';

export interface MetricCard {
  label: string;
  value: string;
  unit?: string;
  trend?: { direction: 'up' | 'down' | 'flat'; pct?: number; label?: string };
  tone?: 'neutral' | 'warn' | 'good' | 'bad';
}

export interface BarSeries {
  name: string;
  /** One value per category, parallel with the categories array. */
  values: number[];
  color?: string;
  /** Optional per-category bar colours (parallel to values); overrides `color` per bar. */
  colors?: string[];
  unit?: string;
  /** When normalized: original = displayed * scale + offset. */
  scale?: number;
  offset?: number;
}

export interface ChartSeries {
  name: string;
  color?: string;
  /** Tuples of [timestamp(ms), value]. */
  points: [number, number][];
  /** Which Y axis to bind to when the chart is dual-axis. Default 'left'. */
  axis?: 'left' | 'right';
  /** Display unit for tooltips (e.g. "ppm", "ug/m^3"). */
  unit?: string;
  /**
   * If the chart is normalized (3+ parameters with different units), this is the
   * factor used to recover the original value: original = displayed * scale + offset.
   */
  scale?: number;
  offset?: number;
}

/** A single point in a scatter / correlation chart. */
export interface ScatterPoint {
  x: number;
  y: number;
}

/** One slice of a donut chart. */
export interface DonutSegment {
  label: string;
  value: number;
  color?: string;
}

/** A coloured band of a gauge: values up to `upTo` take this tone. */
export interface GaugeZone {
  upTo: number;
  tone: 'good' | 'warn' | 'bad';
}

/** Box-plot statistics for one category (typically one device). */
export interface BoxStat {
  /** Category label (device name). */
  category: string;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  /** Optional mean tick. */
  mean?: number;
  /** Optional sample size. */
  n?: number;
}

/**
 * One "what does this mean for humans" score — translates a raw sensor reading
 * into a comprehensible impact (productivity, sleep, respiratory).
 */
export interface HealthScore {
  label: string;
  /** The driver reading, formatted (e.g. "1180 ppm CO₂"). */
  basis: string;
  /** Negative percentage impact, e.g. -12 = 12% worse than baseline. */
  impactPct: number;
  tone: 'good' | 'warn' | 'bad';
  body: string;
}

/**
 * A file the user uploaded for the agent to read alongside sensor data.
 * Parsed client-side from CSV / TSV / Excel; exposed to the model only when
 * the `read_uploaded_dataset` tool is called (it's never auto-included).
 */
export interface UploadedDataset {
  filename: string;
  uploadedAt: number;
  headers: string[];
  /** Row data; cells are coerced to string | number | null at parse time. */
  rows: (string | number | null)[][];
}

/** One actionable recommendation in an Actions block. */
export interface ActionItem {
  priority: 'high' | 'med' | 'low';
  title: string;
  body: string;
  /** Sensor parameter the action relates to (drives the icon/color). */
  parameter?: string;
}

export type RenderBlock =
  | { kind: 'metrics'; title?: string; cards: MetricCard[] }
  | {
      kind: 'line-chart';
      title: string;
      series: ChartSeries[];
      /** Default unit if all series share one. */
      unit?: string;
      /** When set, draw a right-side Y axis labelled with this unit. */
      rightUnit?: string;
      /** True if values were normalized (all series share a synthetic 0-100 axis). */
      normalized?: boolean;
      subtitle?: string;
      /** Render as a filled area chart instead of a plain line. */
      area?: boolean;
    }
  | {
      kind: 'bar-chart';
      title: string;
      /** Bar category labels (device names, time-of-day buckets, etc.). */
      categories: string[];
      /** One bar group per series, typically one entry per compared parameter. */
      series: BarSeries[];
      /** Default unit if all series share one. */
      unit?: string;
      /** Set when 2+ series with mixed units were normalized to 0–100. */
      normalized?: boolean;
      subtitle?: string;
      /** Bar direction. 'horizontal' (default) or 'vertical' (columns). */
      orientation?: 'horizontal' | 'vertical';
    }
  | {
      kind: 'scatter-chart';
      title: string;
      subtitle?: string;
      /** Axis labels (parameter names). */
      xLabel: string;
      yLabel: string;
      /** Units shown in tooltips / axis ticks. */
      xUnit?: string;
      yUnit?: string;
      color?: string;
      points: ScatterPoint[];
    }
  | {
      kind: 'donut-chart';
      title: string;
      subtitle?: string;
      /** Unit of the segment values, shown in tooltips. */
      unit?: string;
      segments: DonutSegment[];
    }
  | {
      kind: 'gauge';
      title: string;
      subtitle?: string;
      value: number;
      min: number;
      max: number;
      unit?: string;
      /** Optional coloured threshold bands, ascending by `upTo`. */
      zones?: GaugeZone[];
    }
  | {
      kind: 'box-plot';
      title: string;
      subtitle?: string;
      stats: BoxStat[];
      unit?: string;
      color?: string;
    }
  | {
      kind: 'health-impact';
      title?: string;
      subtitle?: string;
      scores: HealthScore[];
    }
  | {
      kind: 'actions';
      title?: string;
      subtitle?: string;
      items: ActionItem[];
    }
  | {
      kind: 'download';
      title: string;
      filename: string;
      /** The file content as a string. Blob URL is constructed on click. */
      content: string;
      mime: string;
      byteCount: number;
      description?: string;
    }
  | { kind: 'callout'; tone: 'info' | 'warn' | 'good'; title: string; body: string };

/** A tool execution result. `display` is rendered by the UI as a block. */
export interface ToolResult {
  /** Compact data the model will continue reasoning over. */
  data: unknown;
  /** Optional render block(s) that the UI shows inline in the message. */
  display?: RenderBlock[];
  /** Optional human-friendly note appended to the model context. */
  note?: string;
}

export type ToolExecutor = (args: Record<string, unknown>) => Promise<ToolResult>;

/** Map of tool name -> client-side executor, built per data-context snapshot. */
export type AgentToolExecutors = Record<string, ToolExecutor>;

/**
 * Resolve the agent chat endpoint URL. In production the browser hits the
 * Spring Boot proxy (same origin family as every other dashboard API call),
 * which forwards to the Node AI server on localhost, so the model API key
 * never reaches the browser. For local development, set REACT_APP_AI_API_URL
 * (for example http://localhost:4000) to point straight at the Node server.
 * @returns Absolute URL of the agent chat endpoint.
 */
export function getAiChatUrl(): string {
  const override = process.env.REACT_APP_AI_API_URL;
  if (override && override.trim()) {
    return `${override.trim().replace(/\/+$/, '')}/chat`;
  }
  return `${DEV_NEW_DOMAIN_URL}/ai/chat`;
}
